import type { FC } from "react";
import {
  MessageSquare,
  ThumbsDown,
  ThumbsUp,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import type { KpiItem, RichTextSegment } from "../Types/ListeningReportTypes";
import { CardTitle, HelpDot, REPORT_KEY } from "./ReportPrimitives";

const KPI_ICONS: Record<KpiItem["icon"], LucideIcon> = {
  messages: MessageSquare,
  positive: ThumbsUp,
  negative: ThumbsDown,
  risk: TriangleAlert,
};

const KpiCard: FC<{ kpi: KpiItem }> = ({ kpi }) => {
  const Icon = KPI_ICONS[kpi.icon];
  const TrendIcon = kpi.trend?.direction === "up" ? TrendingUp : TrendingDown;

  return (
    <div className="br__kpi">
      <div className="br__kpi-label">
        {kpi.label}
        <HelpDot text={kpi.help} />
      </div>
      <span className="br__icon-tile">
        <Icon className="br-ic" aria-hidden />
      </span>
      <div className="br__kpi-value">{kpi.value}</div>
      <div className={`br__kpi-trend${kpi.trend ? ` br__kpi-trend--${kpi.trend.tone}` : ""}`}>
        {kpi.trend && <TrendIcon className="br-ic" aria-hidden />}
        <span>{kpi.detail}</span>
        <span className="br__kpi-compare">{kpi.compare}</span>
      </div>
    </div>
  );
};

const renderSegment = (segment: RichTextSegment, index: number) => {
  switch (segment.tone) {
    case "strong":
      return <strong key={index}>{segment.text}</strong>;
    case "pos":
    case "neg":
      return (
        <strong key={index} className={`br__${segment.tone}`}>
          {segment.text}
        </strong>
      );
    case "mark":
      return <mark key={index}>{segment.text}</mark>;
    default:
      return <span key={index}>{segment.text}</span>;
  }
};

interface SummarySectionProps {
  kpis: KpiItem[];
  readout: RichTextSegment[][];
}

const SummarySection: FC<SummarySectionProps> = ({ kpis, readout }) => {
  const { t } = useTranslation();

  return (
    <section className="br__panel" id="br-summary" data-pdf-block="br-summary" data-pdf-splittable aria-label={t(`${REPORT_KEY}.tabs.summary`, "Summary")}>
      <div className="br__kpis">
        {kpis.map(kpi => (
          <KpiCard key={kpi.id} kpi={kpi} />
        ))}
      </div>

      <div className="br__card br__readout">
        <CardTitle help={t(`${REPORT_KEY}.readout.help`, "Summary of the main findings")}>
          {t(`${REPORT_KEY}.readout.title`, "Executive Readout")}
        </CardTitle>
        {readout.map((paragraph, index) => (
          <p key={index}>{paragraph.map(renderSegment)}</p>
        ))}
      </div>
    </section>
  );
};

export default SummarySection;
